/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Grid2 } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Bar, Pie } from 'react-chartjs-2';

import { getDataByGroup } from 'js/actions/meterDashboardActions';
import {
  Portlet,
  PortletHeader,
  PortletLabel,
  PortletContent,
} from 'components';
import palette from 'theme/customColors';
import styles from './styles';

// Import Chart.js components
import {
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';
import { Chart as ChartJS } from 'chart.js';

// Register the necessary chart components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

// Custom styles using Material-UI's makeStyles
const useStyles = makeStyles(styles);

/**
 * Component to display charts showing amounts and consumption by group. 📊
 * @param {Object} props - Properties passed to the component
 * @param {string} props.corpId - Corporate ID 🏢
 * @param {string} props.meterId - Meter ID ⚡
 * @param {string} props.titleAmount - Title for the "Amount" chart 💵
 * @param {string} props.titleConsumption - Title for the "Consumption" chart ⚡
 */
const AmountByGroupChart = ({
  corpId,
  meterId,
  titleAmount,
  titleConsumption,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();

  // State for holding chart data and column sizes 📊
  const [data1, setData1] = useState(null);
  const [data2, setData2] = useState(null);
  const [numCols1, setNumCols1] = useState(8);
  const [numCols2, setNumCols2] = useState(4);

  // Fetch data when component mounts or dependencies change 🔄
  useEffect(() => {
    dispatch(getDataByGroup(corpId, meterId, handleDataResponse));
  }, [dispatch, corpId, meterId]);

  /**
   * Handles the response data, processes it, and updates the state 📝
   * @param {Array} data - The response data containing amounts and consumption information 📊
   */
  const handleDataResponse = (data) => {
    if (!data) {
      setData1([]);
      setData2([]);
      setNumCols1(8);
      setNumCols2(4);
      return;
    }

    const isLargeDataset = data.length > 8; // Adjust column sizes based on dataset size 📐
    setNumCols1(isLargeDataset ? 12 : 8);
    setNumCols2(isLargeDataset ? 12 : 4);

    // Extract labels and values from the data 📋
    const labels = data.map((item) => item.name);
    const values = data.map((item) => item.total);
    const labels2 = data.map(
      (item) => `${item.name}: ${item.total_kwh.toLocaleString()} kWh`
    );
    const values2 = data.map((item) => item.total_kwh);

    // Set chart data for "Amount" and "Consumption" 📊
    setData1({
      labels,
      datasets: [
        {
          label: intl.formatMessage({
            id: 'app.meter.board.amountByGroup.dataset.labels.amount',
          }),
          backgroundColor: getColors(values.length),
          data: values,
        },
      ],
    });

    setData2({
      labels: labels2,
      datasets: [
        {
          label: intl.formatMessage({
            id: 'app.meter.board.consumptionByGroup.dataset.labels.amount',
          }),
          backgroundColor: getColors(values2.length),
          data: values2,
        },
      ],
    });
  };

  /**
   * Returns an array of colors based on the length of the dataset 🎨
   * @param {number} length - The length of the dataset to determine the number of colors 🌈
   * @returns {Array} An array of color strings 🎨
   */
  const getColors = (length) => {
    const paletteColors = [
      '#0074D9',
      '#FF4136',
      '#2ECC40',
      '#FF851B',
      '#7FDBFF',
      '#B10DC9',
      '#FFDC00',
      '#001f3f',
      '#39CCCC',
      '#01FF70',
      '#85144b',
      '#F012BE',
      '#3D9970',
      '#111111',
      '#AAAAAA',
    ];
    return Array.from(
      { length },
      (_, i) => paletteColors[i % paletteColors.length]
    );
  };

  // Chart options for customizing tooltips, axes, etc. ⚙️
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      enabled: true,
      mode: 'index',
      borderWidth: 1,
      borderColor: palette.border,
      backgroundColor: palette.common.white,
      titleFontColor: palette.text.primary,
      bodyFontColor: palette.text.secondary,
      callbacks: {
        label: (tooltipItem, data) =>
          `${data.datasets[tooltipItem.datasetIndex].label} $${tooltipItem.value.toLocaleString()}`,
      },
    },
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            callback: (value) => `$${value.toLocaleString()}`,
          },
        },
      ],
    },
  };

  return (
    <Grid2 container spacing={2}>
      {/* Chart for Amount 💵 */}
      <Grid2 size={{ lg: numCols1, md: numCols1, xl: numCols1, xs: 12 }}>
        <Portlet>
          <PortletHeader noDivider>
            <PortletLabel title={titleAmount} />
          </PortletHeader>
          <PortletContent>
            <div className={classes.chartWrapper}>
              {data1 && <Bar data={data1} options={chartOptions} />}
            </div>
          </PortletContent>
        </Portlet>
      </Grid2>

      {/* Chart for Consumption ⚡ */}
      <Grid2 size={{ lg: numCols2, md: numCols2, xl: numCols2, xs: 12 }}>
        <Portlet>
          <PortletHeader noDivider>
            <PortletLabel title={titleConsumption} />
          </PortletHeader>
          <PortletContent>
            <div className={classes.chartWrapper}>
              {data2 && <Pie data={data2} options={chartOptions} />}
            </div>
          </PortletContent>
        </Portlet>
      </Grid2>
    </Grid2>
  );
};

export default AmountByGroupChart;
